<template>
  <div>
    <transition
      enter-class="opacity-0"
      enter-active-class="ease-out duration-300 transition"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-active-class="transition ease-in duration-300"
      leave-to-class="opacity-0"
      @before-enter="addClass"
      @after-leave="removeClass"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 transition-opacity max-w-screen max-h-screen overflow-hidden w-full h-full z-998"
      >
        <div class="absolute inset-0 bg-neutral-900 opacity-75" />
      </div>
    </transition>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-75"
      leave-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-999 overflow-y-auto lg:overflow-y-scroll"
        @click="closeBackdrop"
        @keydown.esc="closeBackdrop"
      >
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <span
            class="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <div
            class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transition-all sm:my-20 sm:align-middle sm:max-w-lg w-full"
            :class="{
              'bg-white': !darkMode,
              'bg-ocean-950': darkMode,
              'sm:max-w-lg': size === 'sm',
              'sm:max-w-xl': size === 'md',
              'sm:max-w-2xl': size === 'lg',
              'sm:max-w-3xl': size === 'xl',
              'sm:max-w-4xl': size === '2xl',
            }"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="headerId"
            data-testid="vetster-modal"
            @click.stop=""
          >
            <header class="px-4 py-6 bg-ocean-950 sm:px-6 rounded-t-lg">
              <div class="flex items-start justify-between space-x-3">
                <div class="space-y-1">
                  <h2
                    :id="headerId"
                    class="text-xl leading-7 font-medium text-white"
                  >
                    {{ header }}
                  </h2>
                  <p
                    v-if="subHeader"
                    class="text-base text-ocean-200 leading-5"
                  >
                    {{ subHeader }}
                  </p>
                </div>
                <div class="h-7 flex items-center">
                  <button
                    :aria-label="$t('app.close-panel')"
                    class="text-white hover:text-ocean-200 transition"
                    type="button"
                    @click="close"
                  >
                    <svg
                      class="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </header>
            <div class="px-4 sm:px-6 sm:pt-6 pt-5 pb-4">
              <slot ref="default" />
            </div>
            <div
              v-if="hasFooterButton"
              class="shrink-0 px-4 sm:px-6"
            >
              <div class="space-x-3 flex justify-end pb-5 pt-5 border-t border-neutral-200">
                <vetster-button-legacy
                  v-if="cancelLabel"
                  variant="outlineGray"
                  @click.prevent="clickCancel"
                >
                  {{ cancelLabel }}
                </vetster-button-legacy>
                <vetster-button-legacy
                  v-if="actionLabel"
                  variant="ocean"
                  :aria-label="actionLabel"
                  class="flex items-center justify-center"
                  data-testid="modal-submit"
                  :disabled="loading || disableSubmit"
                  @click.prevent="clickSubmit"
                >
                  <span>{{ actionLabel }}</span>
                  <fa
                    v-if="loading"
                    :icon="['fal', 'circle-notch']"
                    class="text-xl text-white-500 ml-2 fa-spin"
                  />
                </vetster-button-legacy>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import size from 'lodash/size';

import { uuid } from '@/helpers/uuid';
import { i18n } from '@/i18n';
import CloseOnEscape from '@/mixins/CloseOnEscape';

export default {
  name: 'VetsterModal',

  mixins: [CloseOnEscape],

  props: {
    id: {
      type: String,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    darkMode: {
      type: Boolean,
      default: false,
    },
    isBackdropClose: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'sm',
    },
    header: {
      type: String,
      default: undefined,
    },
    subHeader: {
      type: String,
      default: undefined,
    },
    actionLabel: {
      type: String,
      default: undefined,
    },
    cancelLabel: {
      type: String,
      default: () => i18n.t('common.cancel'),
    },
    disableSubmit: {
      type: Boolean,
      default: false,
    },
    listensToSlot: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: false,
      confirmed: false,
    };
  },

  computed: {
    hasFooterButton() {
      return size(this.actionLabel) || size(this.cancelLabel);
    },
    headerId() {
      return `modal-header-${this.id || uuid()}`;
    },
  },

  beforeUpdate() {
    this.listenToSlot();
  },

  mounted() {
    this.listenToSlot();
  },

  methods: {
    addClass() {
      document.body.classList.add('modal-open');
    },
    removeClass() {
      document.body.classList.remove('modal-open');
    },
    clickSubmit() {
      this.$emit('submit');
    },
    clickCancel() {
      this.$emit('cancel');
      this.close();
    },
    closeBackdrop() {
      if (this.isBackdropClose) {
        this.close();
      }
    },
    open() {
      this.isOpen = true;
      this.confirmed = false;
      if (this.isBackdropClose) {
        this.closeOnEscape(this.close);
      }
    },
    close() {
      this.$emit('close');
      this.isOpen = false;
      this.destroyCloseOnEscape();
    },
    hasDefaultSlot() {
      return !!this.$slots.default;
    },
    listenToSlot() {
      if (this.listensToSlot) {
        if (this.hasDefaultSlot() && !this.isOpen) {
          this.open();
        } else if (!this.hasDefaultSlot() && this.isOpen) {
          this.close();
        }
      }
    },
  },
};
</script>

<style lang="scss">
.modal-open {
  // TODO: temporarily remove to avoid jumping when showing share modal
  // @media (min-width: 1024px) {
  //   padding-right: 15px;

  //   //stops nav from dancing around
  //   .nav-overflow {
  //     padding-right: 15px;
  //   }
  // }

  overflow: hidden;
  position: fixed;
  width: 100vw;
}
</style>
